import { useState } from 'react';
import { Link } from 'react-router-dom';
import { I } from '../icons.jsx';
import { TextField, Footer, AuthDecor, VividWordmark } from '../primitives.jsx';
import { api } from '../api.js';
import { LangSwitch, useLang } from '../i18n.jsx';

// D3: "Forgot password". Posts the email to /auth/request-password-reset,
// which emails a link to /reset-password?token=… on this auth UI.
//
// The answer is the same whether or not the address has an account (the
// backend never says, so nobody can probe for accounts), and the same when
// email delivery is not configured: the page cannot know the mail never
// left, so it always says where to turn if nothing arrives, the way the
// sign-up flow does when it cannot email the code.
//
// English or Arabic (``?lang=``, see i18n.jsx); the emailed link opens the
// reset page in the same language.
export default function ForgotPasswordPage() {
  const { lang, dir, t, setLang } = useLang();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (ev) => {
    ev.preventDefault();
    const value = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      setError(t('forgot.invalidEmail'));
      return;
    }
    setError('');
    setLoading(true);
    try {
      await api.requestPasswordReset(value, lang);
      setDone(true);
    } catch (err) {
      if (err.status === 429) {
        setError(t('forgot.tooMany'));
      } else if (err.status === 422) {
        setError(t('forgot.invalidEmail'));
      } else {
        // A server error must not tell the visitor anything about the
        // account either; show the neutral answer with the fallback.
        setDone(true);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div dir={dir} lang={lang} className="min-h-screen flex flex-col bg-white">
      <div className="flex-1 grid lg:grid-cols-2">
        <div className="flex flex-col px-6 lg:px-12">
          <div className="h-20 flex items-center justify-between">
            <VividWordmark />
            <LangSwitch lang={lang} setLang={setLang} />
          </div>

          <div className="flex-1 flex items-start pt-[8vh] justify-center">
            {done ? (
              <div className="w-full max-w-[420px]">
                <div className="inline-flex w-12 h-12 rounded-full bg-stone-100 items-center justify-center text-ink">
                  <I.MailCheck size={22} />
                </div>
                <h1 className="mt-5 text-[28px] font-bold tracking-tight text-ink">{t('forgot.checkEmail')}</h1>
                <p role="status" className="mt-2 text-[15px] text-stone-500">{t('forgot.neutral')}</p>
                <p className="mt-4 text-[13px] text-stone-500">
                  {t('forgot.nothingArrived')}
                  {import.meta.env.DEV && " (Dev: the link is logged to the auth backend's stdout.)"}
                </p>
                <Link to="/login" className="btn-primary w-full mt-8 inline-flex justify-center">{t('common.signIn')}</Link>
              </div>
            ) : (
              <form onSubmit={submit} className="w-full max-w-[420px]" noValidate>
                <div className="inline-flex w-12 h-12 rounded-full bg-stone-100 items-center justify-center text-ink">
                  <I.Key size={22} />
                </div>
                <h1 className="mt-5 text-[28px] font-bold tracking-tight text-ink">{t('forgot.title')}</h1>
                <p className="mt-2 text-[15px] text-stone-500">
                  {t('forgot.intro')}
                </p>
                <div className="mt-7">
                  <TextField label={t('common.email')} type="email" value={email} onChange={setEmail}
                    error={error || undefined} autoComplete="email" placeholder="you@company.com" />
                </div>
                <div className="pt-7">
                  <button type="submit" disabled={!email.trim() || loading} className="btn-primary w-full">
                    {loading ? t('forgot.sending') : t('forgot.send')}
                  </button>
                  <Link to="/login" className="mt-3 block w-full text-center text-[13px] text-stone-500 hover:text-ink">
                    {t('common.backToSignIn')}
                  </Link>
                </div>
              </form>
            )}
          </div>

          <Footer t={t} />
        </div>

        <div className="hidden lg:block"><AuthDecor t={t} /></div>
      </div>
    </div>
  );
}
